import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getActiveChannelDetail } from "@/lib/api";

/**
 * Inline modal for viewing per-video view counts for one of the user's
 * tracked channels. Reuses the same `/active-channels/{id}` detail
 * endpoint that powers the full detail page — no extra backend work.
 */
function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return String(num);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function parseDur(iso) {
  if (!iso) return 0;
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!m) return 0;
  return (+m[1] || 0) * 3600 + (+m[2] || 0) * 60 + (+m[3] || 0);
}

function fmtDur(sec) {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const SORTS = [
  { key: "recent", label: "Recent" },
  { key: "views", label: "Views" },
  { key: "likes", label: "Likes" },
];

export default function ChannelVideosModal({ channel, onClose }) {
  const [videos, setVideos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getActiveChannelDetail(channel.channel_id);
        if (cancelled) return;
        setVideos(Array.isArray(data?.videos) ? data.videos : []);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.detail || "Couldn't load videos";
        setError(typeof msg === "string" ? msg : "Couldn't load videos");
        toast.error(typeof msg === "string" ? msg : "Couldn't load videos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [channel.channel_id]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const sortedVideos = useMemo(() => {
    if (!videos) return [];
    const arr = [...videos];
    if (sort === "views") {
      arr.sort((a, b) => (Number(b.views) || 0) - (Number(a.views) || 0));
    } else if (sort === "likes") {
      arr.sort((a, b) => (Number(b.likes) || 0) - (Number(a.likes) || 0));
    } else {
      arr.sort((a, b) =>
        String(b.published_at || "").localeCompare(String(a.published_at || "")),
      );
    }
    return arr;
  }, [videos, sort]);

  const totalViews = useMemo(
    () => (videos || []).reduce((s, v) => s + (Number(v.views) || 0), 0),
    [videos],
  );

  return (
    <div
      data-testid="channel-videos-modal-overlay"
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        data-testid="channel-videos-modal"
        className="bg-zinc-950 border border-yellow-400/40 rounded-sm w-full max-w-4xl my-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-1">
              // RECENT VIDEOS · VIEW COUNTS
            </div>
            <h2 className="font-display text-2xl font-black text-white truncate">
              {channel.name}
            </h2>
            {!loading && videos && videos.length > 0 && (
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1">
                {videos.length} uploads · {fmtNum(totalViews)} total views · snapshot from last sync
              </div>
            )}
          </div>
          <button
            data-testid="channel-videos-modal-close"
            onClick={onClose}
            className="font-mono text-zinc-400 hover:text-yellow-400 text-2xl leading-none shrink-0"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {!loading && videos && videos.length > 0 && (
          <div className="px-6 pt-4 flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mr-1">
              sort by
            </span>
            {SORTS.map((s) => (
              <button
                key={s.key}
                data-testid={`channel-videos-sort-${s.key}`}
                onClick={() => setSort(s.key)}
                className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${
                  sort === s.key
                    ? "border-yellow-400 text-yellow-400"
                    : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div className="p-6">
          {loading && (
            <div className="text-center py-10" data-testid="channel-videos-loading">
              <div className="font-mono text-sm text-yellow-400">
                [recon] loading uploads<span className="blink">_</span>
              </div>
            </div>
          )}

          {!loading && error && (
            <div
              className="border border-red-700/50 bg-red-950/30 rounded-sm p-5 text-red-200 text-sm"
              data-testid="channel-videos-error"
            >
              {error}
            </div>
          )}

          {!loading && !error && videos && videos.length === 0 && (
            <div
              className="border border-dashed border-zinc-800 rounded-sm p-10 text-center"
              data-testid="channel-videos-empty"
            >
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
                // NO UPLOADS YET
              </div>
              <p className="text-zinc-400 text-sm max-w-md mx-auto">
                YouTube hasn&apos;t returned any videos for this channel yet.
                Post something and hit ↻ refresh on the card.
              </p>
            </div>
          )}

          {!loading && !error && videos && videos.length > 0 && (
            <div
              className="overflow-x-auto border border-zinc-800 rounded-sm"
              data-testid="channel-videos-table-wrap"
            >
              <table className="w-full text-sm">
                <thead className="bg-black/40 text-zinc-300 font-mono text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2.5">Title</th>
                    <th className="text-right px-3 py-2.5">Views</th>
                    <th className="text-right px-3 py-2.5">Likes</th>
                    <th className="text-right px-3 py-2.5">Length</th>
                    <th className="text-right px-4 py-2.5">Posted</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVideos.map((v) => (
                    <tr
                      key={v.video_id}
                      className="border-t border-zinc-900 hover:bg-zinc-900/40"
                      data-testid={`channel-videos-row-${v.video_id}`}
                    >
                      <td className="px-4 py-2.5">
                        <a
                          href={`https://www.youtube.com/watch?v=${v.video_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-zinc-100 hover:text-yellow-400 transition-colors"
                        >
                          {v.title}
                        </a>
                      </td>
                      <td
                        className="px-3 py-2.5 text-right text-zinc-100 font-mono tabular-nums font-semibold"
                        data-testid={`channel-videos-row-views-${v.video_id}`}
                      >
                        {fmtNum(v.views)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-zinc-400 font-mono tabular-nums">
                        {fmtNum(v.likes)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-zinc-500 font-mono">
                        {fmtDur(parseDur(v.duration))}
                      </td>
                      <td className="px-4 py-2.5 text-right text-zinc-500 font-mono">
                        {fmtDate(v.published_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
